import * as React from "react";

import {
  POLICY_VERSION,
  type ConsentDecision,
} from "./consent-config";
import {
  getOrCreateConsentId,
  isDecisionCurrent,
  readConsentCookie,
  writeConsentCookie,
} from "./consent-storage";
import { recordConsent } from "./recordConsent";

/**
 * SPEC-COOKIE-2 — Consent context. Banner, footer link, and any
 * future analytics loader read the current decision from here.
 *
 * Lifecycle: on mount we read the cookie ONCE (no DB call) and set
 * `needsDecision` accordingly. acceptAll / rejectAll / saveCustom each
 * write the cookie synchronously (so the next render hides the banner)
 * and fire `recordConsent` in the background (best-effort, see
 * recordConsent.ts).
 *
 * Mount INSIDE AuthProvider (so `supabase.auth.getUser()` can attach
 * the user) but OUTSIDE Routes (so the banner shows on `/login` and
 * `/cerez-politikasi` too).
 */

export interface ConsentContextValue {
  decision: ConsentDecision | null;
  /** True when no current decision exists → banner should show. */
  needsDecision: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCustom: (categories: {
    analytics: boolean;
    marketing: boolean;
  }) => void;
  /** Open the manage modal (footer "Çerez tercihleri" link). */
  openManager: () => void;
  managerOpen: boolean;
  setManagerOpen: (open: boolean) => void;
}

const ConsentContext = React.createContext<ConsentContextValue | null>(null);

export function useConsent(): ConsentContextValue {
  const ctx = React.useContext(ConsentContext);
  if (!ctx) {
    throw new Error(
      "useConsent must be used within <ConsentProvider>",
    );
  }
  return ctx;
}

export interface ConsentProviderProps {
  children: React.ReactNode;
}

export function ConsentProvider({ children }: ConsentProviderProps) {
  const [decision, setDecision] = React.useState<ConsentDecision | null>(
    () => readConsentCookie(),
  );
  const [managerOpen, setManagerOpen] = React.useState(false);

  const persist = React.useCallback(
    (next: ConsentDecision) => {
      writeConsentCookie(next);
      setDecision(next);
      // Fire-and-forget; recordConsent swallows its own errors so the
      // promise never rejects.
      void recordConsent(next);
    },
    [],
  );

  const buildDecision = React.useCallback(
    (
      analytics: boolean,
      marketing: boolean,
      action: ConsentDecision["action"],
    ): ConsentDecision => ({
      necessary: true,
      analytics,
      marketing,
      action,
      policyVersion: POLICY_VERSION,
      consentId: getOrCreateConsentId(),
      decidedAt: new Date().toISOString(),
    }),
    [],
  );

  const acceptAll = React.useCallback(() => {
    persist(buildDecision(true, true, "accept_all"));
    setManagerOpen(false);
  }, [buildDecision, persist]);

  const rejectAll = React.useCallback(() => {
    persist(buildDecision(false, false, "reject"));
    setManagerOpen(false);
  }, [buildDecision, persist]);

  const saveCustom = React.useCallback(
    (categories: { analytics: boolean; marketing: boolean }) => {
      persist(
        buildDecision(categories.analytics, categories.marketing, "custom"),
      );
      setManagerOpen(false);
    },
    [buildDecision, persist],
  );

  const openManager = React.useCallback(() => setManagerOpen(true), []);

  const value = React.useMemo<ConsentContextValue>(
    () => ({
      decision,
      needsDecision: !isDecisionCurrent(decision),
      acceptAll,
      rejectAll,
      saveCustom,
      openManager,
      managerOpen,
      setManagerOpen,
    }),
    [
      decision,
      acceptAll,
      rejectAll,
      saveCustom,
      openManager,
      managerOpen,
    ],
  );

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}
